import { GridEnrichedColDef, GridRowParams, GridActionsCellItem } from '@mui/x-data-grid-pro';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useStore } from '../../../../hooks/useStore';
import { Chip, ContentHead } from '../../../../lib/components';
import { useDialog } from '../../../../lib/components/compound/dialogs/confirmationDialog/useDialog';
import { ContentBox } from '../../../../lib/components/compound/pages/contentBox';
import { EditIcon, DeleteIcon } from '../../../../lib/icons';
import { AddButton } from '../shared/addButton';
import { CompanyTable } from '../shared/companyTable';
import { ConfirmDeleteDialog } from '../shared/confirmDelete';

export const CompanyLocationsData = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyStore } = useStore();
  const dialog = useDialog<string>();

  const locations =
    companyStore.locations?.map((location) => ({
      id: location.id,
      name: location.name,
      active: location.active
    })) || [];

  const columns: Array<GridEnrichedColDef> = [
    {
      field: 'name',
      headerName: t('company:LOCATION'),
      flex: 3
    },
    {
      field: 'active',
      headerName: t('common:STATUS'),
      flex: 2,
      type: 'boolean',
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Chip
          label={params.value ? t('employee:ACTIVE') : t('employee:INACTIVE')}
          color={params.value ? 'default' : 'secondary'}
        />
      )
    },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key={`${params.id}-edit`}
          icon={<EditIcon />}
          showInMenu
          label={t('common:EDIT')}
          onClick={() => navigate(`./${params.id}`)}
          placeholder=""
        />,
        <GridActionsCellItem
          key={`${params.id}-delete`}
          icon={<DeleteIcon />}
          showInMenu
          label={t('common:DELETE')}
          onClick={() => dialog.setDialogRelevantValue(params.id as string)}
          placeholder=""
        />
      ]
    }
  ];

  useEffect(() => {
    if (!companyStore.company) return;
    const abortController = new AbortController();

    const fetchLocations = async () => {
      try {
        await companyStore.fetchLocations();
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error(error);
        }
      }
    };
    fetchLocations();

    return () => abortController.abort();
  }, [companyStore.company]);

  return (
    <ContentBox
      headComponent={
        <ContentHead
          headline={t('company:LOCATIONS', { count: companyStore.locations?.length })}
          showActions={true}
          renderActions={() => <AddButton onClick={() => navigate('new')} />}
        />
      }
    >
      <CompanyTable
        rows={locations}
        columns={columns}
        onRowDoubleClick={(row) => {
          navigate(`${row.id}`);
        }}
      />
      <ConfirmDeleteDialog
        type="location"
        open={dialog.dialogProps.open}
        onClose={dialog.dialogProps.onClose}
        onConfirm={() =>
          dialog?.dialogRelevantValue
            ? companyStore.deleteLocation(dialog?.dialogRelevantValue)
            : console.error('No location id provided')
        }
      />
    </ContentBox>
  );
});
